import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, ErrorAlert, PageHeader, Spinner } from '../components/ui';

export function SettingsPage() {
  const { data, error, loading, reload } = useAsync(() => api.settings(), []);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [cronBusy, setCronBusy] = useState(false);
  const [cronNote, setCronNote] = useState<string | null>(null);

  const runCron = async () => {
    setCronBusy(true);
    setSaveError(null);
    setCronNote(null);
    try {
      const res = await api.cronRun();
      setCronNote(`Cron: opublikowano ${res.result.published}, pominięto ${res.result.skipped}, błędy ${res.result.failed}.`);
    } catch (err) {
      setSaveError(err);
    } finally {
      setCronBusy(false);
    }
  };

  useEffect(() => {
    if (data) setDrafts(Object.fromEntries(data.map((s) => [s.key, s.value])));
  }, [data]);

  const save = async (key: string) => {
    setBusyKey(key);
    setSaveError(null);
    setSavedKey(null);
    try {
      await api.settingUpdate(key, drafts[key] ?? '');
      setSavedKey(key);
      reload();
    } catch (err) {
      setSaveError(err);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <>
      <PageHeader title="Settings" subtitle="Konfiguracja aplikacji. Sekrety nigdy nie są zwracane przez API." />
      {loading && <Spinner />}
      {error && <ErrorAlert error={error} />}
      <ErrorAlert error={saveError} />
      {savedKey && <div className="alert alert--success">Zapisano: {savedKey}</div>}
      {cronNote && <div className="alert alert--success">{cronNote}</div>}

      <Card title="Automatyzacja">
        <p className="muted">
          Cron (co godzinę na Cloudflare) publikuje zaplanowane treści, których czas minął, i czyści wygasłe sesje.
        </p>
        <Button variant="primary" onClick={runCron} disabled={cronBusy}>
          {cronBusy ? 'Uruchamianie…' : 'Uruchom cron teraz'}
        </Button>
      </Card>
      <div style={{ height: 16 }} />

      {data && (
        <Card flush>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 280 }}>Klucz</th>
                <th>Wartość</th>
                <th style={{ width: 90 }}>Grupa</th>
                <th style={{ width: 110, textAlign: 'right' }}>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {data.map((s) => {
                const dirty = drafts[s.key] !== undefined && drafts[s.key] !== s.value;
                return (
                  <tr key={s.key}>
                    <td className="mono">{s.key}</td>
                    <td>
                      {s.key === 'social_seo.platform_rules' ? (
                        <textarea
                          className="textarea"
                          rows={8}
                          spellCheck={false}
                          aria-label="Platform social SEO rules JSON"
                          value={drafts[s.key] ?? ''}
                          onChange={(e) => setDrafts((d) => ({ ...d, [s.key]: e.target.value }))}
                        />
                      ) : (
                        <input
                          className="input"
                          value={drafts[s.key] ?? ''}
                          onChange={(e) => setDrafts((d) => ({ ...d, [s.key]: e.target.value }))}
                        />
                      )}
                    </td>
                    <td>
                      <Badge tone="neutral">{s.group}</Badge>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Button size="sm" variant={dirty ? 'primary' : 'default'} disabled={busyKey === s.key || !dirty} onClick={() => save(s.key)}>
                        {busyKey === s.key ? '…' : 'Zapisz'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
